import { useCallback, useMemo, useState } from 'react';
import {
  useGenerateDeposit,
  useValidateDeposit,
} from '@xhype/card-app-sdk';
import { useExecuteTransactions } from '@package/smart-wallet/hooks';
import { useSmartWallet } from '@package/smart-wallet/providers';
import { useAuthStore } from '@package/smart-wallet/store';
import {
  isSignerInitialized,
  parseTransaction,
} from '@package/smart-wallet/utils';
import * as Sentry from '@sentry/react';

export const useHandleCardDeposit = () => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [isDepositLoading, setIsDepositLoading] = useState(false);
  const id = useAuthStore((store) => store.id);
  const { buildTransactions, executeTransactions } = useExecuteTransactions();
  const { smartAccountAddress, isLoading: isLoadingSmartWallet } =
    useSmartWallet();

  /* Mutations */
  const { mutateAsync: createDeposit } = useGenerateDeposit({
    onError: (error) => {
      console.error('=== Deposit generation error: ', error);
      Sentry.captureException({
        message: 'Deposit generation error',
        extra: {
          error,
        },
      });
    },
  });

  const { mutateAsync: validateDeposit } = useValidateDeposit({
    onError: (error) => {
      console.error('=== Deposit validation error: ', error);
      Sentry.captureException({
        message: 'Deposit validation error',
        extra: {
          error,
        },
      });
    },
  });

  /* Memo */
  const isLoading = useMemo(() => {
    return isLoadingSmartWallet || isDepositLoading;
  }, [isLoadingSmartWallet, isDepositLoading]);

  /* Callbacks */
  const handleDeposit = useCallback(
    async (
      amount: string,
      couponCode: string | undefined,
      holderId: number,
      cardId: number,
    ) => {
      try {
        setError(undefined);
        setIsDepositLoading(true);
        isSignerInitialized(smartAccountAddress ?? '');

        if (id === undefined) {
          throw new Error('User not initialized');
        }

        const { data: transactionData } = await createDeposit({
          userId: id,
          amount,
          couponCode,
          holderId,
          cardId,
        });

        const quotes = transactionData.quotes;
        const transactionId = transactionData.txId.toString();

        if (quotes.length === 0) {
          throw new Error('No quotes found');
        }

        const parsedQuotes = parseTransaction(quotes);
        const { transactions } = buildTransactions({
          transactions: parsedQuotes,
        });

        const txHash = await executeTransactions({
          transactions,
        });

        if (!txHash) {
          throw new Error('Transaction hash is undefined');
        }

        const { success } = await validateDeposit({
          transactionId,
          txHash,
        });

        if (!success) {
          throw new Error('Deposit validation failed');
        }

        return txHash;
      } catch (error) {
        console.error('Error sending deposit transaction: ', error);
        setError(`Error sending deposit transaction: ${error}`);
        Sentry.captureException({
          message: 'Error sending deposit transaction',
          extra: {
            error,
          },
        });
        throw new Error(`Error sending deposit transaction: ${error}`);
      } finally {
        setIsDepositLoading(false);
      }
    },
    [
      smartAccountAddress,
      buildTransactions,
      executeTransactions,
      validateDeposit,
      createDeposit,
      id,
    ],
  );

  /* Return */
  return {
    error,
    isLoading,
    handleDeposit,
  };
};
